import React, { useEffect, useState } from "react";
import { Anchor, Box, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { useAppContext } from "../../Context/AppContext";
import classes from "./Footer.module.css";
// Matches the legacy trailer.tpl / MUI Footer's moment('h:mmA')-style clock, pinned to an
// IANA timezone: "UTC" for the UTC line, the office's zone (appData.timeZone) for the local
// one. Without a zone Intl formats in the BROWSER's zone, which is how the "local" clock came
// to show the viewer's time instead of the office's -- support reads that line to know what
// time it is for the tenant, so it has to be office time whoever is looking.
//
// The details row runs this on every render, so it must not throw on bad input:
// Intl.DateTimeFormat throws on an invalid Date AND on an unknown timeZone name. A missing or
// unrecognised zone falls back to the wall-clock hour and minute written into the ISO string,
// which the server already produced in the office zone (PHP format 'c'); only a bare Date with
// a bad zone drops to the browser zone.
export function formatClock(input, timeZone) {
    // `new Date(undefined)` is an Invalid Date, so a payload with no timeLocal/timeGMT lands
    // on the "--:--" guard rather than throwing.
    const date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime()))
        return "--:--";
    if (timeZone) {
        try {
            return intlClock(date, timeZone);
        }
        catch (e) {
            if (!(e instanceof RangeError))
                throw e;
        }
    }
    const wall = typeof input === "string" ? /T(\d{2}):(\d{2})/.exec(input) : null;
    if (wall)
        return wallClock(Number(wall[1]), Number(wall[2]));
    return intlClock(date, undefined);
}
function intlClock(date, timeZone) {
    return new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone,
    })
        .format(date)
        .replace(" ", "");
}
function wallClock(hours, minutes) {
    const h12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${h12}:${String(minutes).padStart(2, "0")}${hours < 12 ? "AM" : "PM"}`;
}
/**
 * One click-to-copy identity row.
 *
 * Declared at module scope, not inside Footer: a component defined in a render
 * body is a new type on every render, so React tears down and rebuilds the
 * subtree each time the parent updates.
 *
 * Copies the FULL value even when a shortened one is displayed -- the task id is
 * shown front-anchored but the Console and AWS both want all 32 characters.
 */
function CopyRow({ label, display, value, copied, setCopied, }) {
    const copyable = Boolean(value);
    const done = copied === label;
    return (React.createElement(UnstyledButton, { className: classes.copyRow, disabled: !copyable, onClick: () => {
            if (!value)
                return;
            navigator.clipboard?.writeText(value).then(() => {
                setCopied(label);
                window.setTimeout(() => setCopied(null), 1200);
            }, 
            // Clipboard access can be denied (insecure context, permission).
            // Failing silently would look identical to success, so leave the
            // hint alone rather than claim it copied.
            () => setCopied(null));
        }, title: copyable ? `Copy ${label}` : undefined },
        React.createElement("span", { className: classes.copyLabel }, label),
        React.createElement("span", { className: classes.copyValue }, display),
        copyable && React.createElement("span", { className: classes.copyHint }, done ? "copied" : "copy")));
}
export default function Footer() {
    const { appData } = useAppContext();
    const [expanded, setExpanded] = useState(false);
    // The chip's headline: a friendly release label when keystone has written one for this
    // build (ISN::getVersionLabel()), otherwise the same buildId/taskId identity the "artifact"
    // and "task" CopyRows below expose in full -- never the raw `build` (VERSION) counter, which
    // answers nothing operational. Full, untruncated form for the hover tooltip; the CopyRow
    // pair below already makes both halves individually copyable when expanded.
    const fullServerLabel = [appData.buildId, appData.taskId].filter(Boolean).join('/');
    // One expression, two renders: the chip headline and the drawer's `build` row. Derived
    // rather than repeated so the collapsed and expanded footer can never name two different
    // builds, which is exactly what happened when the drawer printed VERSION instead.
    const buildDisplay = appData.versionLabel || appData.webServer;
    const chipHeadline = `build ${buildDisplay}`;
    // Expanding the footer adds content BELOW the fold when the page already
    // scrolls, so the click appeared to do nothing except lengthen the scrollbar
    // -- the thing you asked to see stayed off-screen. Scroll to it once the row
    // has actually been laid out.
    //
    // An effect on `expanded`, not a call inside the handler: the row is rendered
    // by this state change, so at handler time scrollHeight is still the old,
    // shorter document.
    useEffect(() => {
        if (!expanded)
            return;
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: reduced ? "auto" : "smooth",
        });
    }, [expanded]);
    const [copied, setCopied] = useState(null);
    // Ported from the legacy footer's "Questions?" column -- live chat is not
    // repeated here, it moved to the header (MainMenu's chat button, same
    // supportOptions/user.chat gate) when this footer was redesigned.
    const supportOptions = appData.supportOptions ?? [];
    const hasSyncStatus = Boolean(appData.accSync?.status || appData.cs2Sync?.status);
    const detailsContent = (React.createElement(Stack, { gap: 12, className: classes.drawer },
        React.createElement(Box, { className: classes.diagnostics },
            React.createElement(Group, { gap: "6px 28px", wrap: "wrap" },
                React.createElement(CopyRow, { copied: copied, setCopied: setCopied, label: "build", display: buildDisplay, value: appData.versionLabel || appData.webServer || undefined }),
                React.createElement(CopyRow, { copied: copied, setCopied: setCopied, label: "artifact", display: appData.buildId || "not deployed", value: appData.buildId || undefined }),
                React.createElement(CopyRow, { copied: copied, setCopied: setCopied, label: "task", display: appData.taskIdShort || "unknown", value: appData.taskId || undefined })),
            React.createElement(Group, { gap: "6px 28px", wrap: "wrap", mt: 8 },
                React.createElement("span", null,
                    "rendered in ",
                    appData.processingSeconds,
                    "s"),
                React.createElement("span", null,
                    formatClock(appData.timeLocal, appData.timeZone),
                    " local"),
                React.createElement("span", null,
                    formatClock(appData.timeGMT, "UTC"),
                    " UTC"))),
        hasSyncStatus && (React.createElement(Group, { gap: "6px 28px", wrap: "wrap" },
            appData.accSync?.status && (React.createElement(Text, { size: "xs", c: "dimmed" },
                "Linked to ",
                React.createElement(Text, { span: true, fw: 600, c: "dimmed" }, "America's Call Center"),
                appData.accSync.lastSync && ` — last sync ${appData.accSync.lastSync}`)),
            appData.cs2Sync?.status && (React.createElement(Text, { size: "xs", c: "dimmed" },
                "Linked to ",
                React.createElement(Text, { span: true, fw: 600, c: "dimmed" }, "CS2")))))));
    // Kendale's call (2026-09-02): help site / system status / email support
    // added noise back to the always-visible row -- simplify to just the phone
    // number, still gated on the backend actually offering phone support.
    const phoneLink = supportOptions.includes("phone") && appData.supportPhoneNumber ? (React.createElement(Anchor, { size: "xs", className: classes.supportLink, href: `tel:${appData.supportPhoneNumber}`, rel: "external" }, appData.supportPhoneNumber)) : null;
    return (
    // Deliberately the same footer vocabulary as the logged-out LoginBuildChip:
    // a gray-2 hairline over white, generous 16/24 padding, xs copy, and a
    // monospace build chip -- rather than the tighter py={6} + <Divider> bar
    // this used to be.
    //
    // The details row also just grows now. It used to be height-reserved via an
    // always-mounted off-screen clone plus a ResizeObserver, so the footer's
    // total height stayed identical whether collapsed or expanded -- which cost
    // a permanent band of empty space at the bottom of every page to buy a
    // no-jump toggle nobody asked for. LoginBuildChip simply expands, and that
    // is the behaviour being matched here.
    React.createElement(Box, { component: "footer", className: classes.root },
        React.createElement(Group, { justify: "space-between", gap: "12px 24px", wrap: "wrap", className: classes.row },
            React.createElement(Group, { gap: 8, wrap: "wrap" },
                React.createElement(Text, { size: "xs", c: "dimmed" },
                    "\u00A9 ",
                    new Date().getFullYear(),
                    " Inspection Support, LLC"),
                React.createElement(Text, { size: "xs", className: classes.sep }, "|"),
                React.createElement(Anchor, { size: "xs", c: "dimmed", href: "https://www.inspectionsupport.com/privacy/", target: "_blank", rel: "external" }, "Privacy"),
                React.createElement(Text, { size: "xs", className: classes.sep }, "|"),
                React.createElement(Anchor, { size: "xs", c: "dimmed", href: "https://www.inspectionsupport.com/isn-terms-of-service/", target: "_blank", rel: "external" }, "Terms"),
                phoneLink && (React.createElement(React.Fragment, null,
                    React.createElement(Text, { size: "xs", className: classes.sep }, "|"),
                    phoneLink))),
            React.createElement(UnstyledButton, { className: classes.chip, onClick: () => setExpanded((v) => !v), "aria-expanded": expanded, "aria-label": "Toggle build details", title: fullServerLabel || undefined },
                React.createElement("span", { className: classes.dot }),
                React.createElement("span", null, chipHeadline),
                React.createElement("span", { "aria-hidden": "true" }, expanded ? "\u25b4" : "\u25be"))),
        expanded && React.createElement(Box, { "data-testid": "footer-details-row" }, detailsContent)));
}
